import { Link } from 'react-router-dom';
import Reveal from '../ui/Reveal';
import { ArrowRight, Check, Droplet } from '../ui/Icons';

/**
 * Full-bleed waterproofing banner followed by a pair of edge-to-edge promo
 * tiles. Every block puts its copy in a box that sits over the photograph.
 */
const PromoBanners = () => (
  <section className="bg-white">
    {/* Waterproofing — full width */}
    <div className="relative min-h-[520px] lg:min-h-[600px] flex items-center overflow-hidden">
      <img src="/images/products/cool-damp-proof-roof-applicator.jpg" alt="An applicator coating a roof with Vertex Cool Damp Proof" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-l from-brand-navy/70 via-brand-navy/20 to-transparent" />
      <div className="relative container flex justify-end py-14">
        <Reveal className="w-full max-w-lg">
          <div className="bg-brand-navy/85 backdrop-blur-md text-white rounded-3xl border border-white/10 shadow-lift p-7 sm:p-10">
            <span className="w-12 h-12 rounded-2xl bg-brand-gold text-brand-ink flex items-center justify-center">
              <Droplet size={22} />
            </span>
            <span className="vp-eyebrow vp-eyebrow-light mt-6">Waterproofing</span>
            <h2 className="vp-heading !text-white mt-3 text-3xl sm:text-4xl">Stop damp at the source, not the stain</h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              Terrace coatings, damp-proof putties, crack-bridging primers and tank sealers — a complete system from roof to foundation, applied by trained crews.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {['Roof & terrace coatings', 'Damp-proof wall putty', 'Bathroom & wet areas', 'Water tank sealing'].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-sm text-white/85">
                  <span className="w-5 h-5 rounded-full bg-brand-gold text-brand-ink flex items-center justify-center flex-shrink-0">
                    <Check size={11} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/WaterProofingAndConstructionChemicals" className="vp-btn vp-btn-gold">
                Waterproofing range
                <ArrowRight />
              </Link>
              <Link to="/services/waterproofing" className="vp-btn vp-btn-ghost">
                Book an inspection
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>

    {/* Two-up tiles — edge to edge */}
    <div className="grid md:grid-cols-2">
      {[
        {
          image: '/images/products/pu-enamel-wood-brush.jpg',
          eyebrow: 'Wood finishes',
          title: 'Let the grain show, or paint it any colour',
          copy: 'Clear PU coats, varnishes and high-gloss enamels that seal timber against moisture and wear.',
          to: '/WoodPaints',
          cta: 'Explore wood finishes',
        },
        {
          image: '/images/products/pu-enamel-railing.jpg',
          eyebrow: 'Metal paints',
          title: 'Rust protection with a finish that shines',
          copy: 'Anti-corrosive primers, gloss enamels and metallic effects for gates, grills and railings.',
          to: '/MetalPaints',
          cta: 'Explore metal paints',
        },
      ].map((t, i) => (
        <Link key={t.to} to={t.to} className="group relative min-h-[440px] lg:min-h-[520px] flex items-end overflow-hidden">
          <img src={t.image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-premium group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/50 via-transparent to-transparent" />
          <Reveal delay={(i as 0 | 1)} className="relative m-5 sm:m-8 lg:m-10 max-w-md">
            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lift p-6 sm:p-7">
              <span className="vp-eyebrow">{t.eyebrow}</span>
              <h3 className="vp-heading mt-3 text-2xl">{t.title}</h3>
              <p className="mt-2.5 text-sm text-brand-muted leading-relaxed">{t.copy}</p>
              <span className="mt-4 vp-link text-sm">
                {t.cta}
                <ArrowRight size={14} />
              </span>
            </div>
          </Reveal>
        </Link>
      ))}
    </div>
  </section>
);

export default PromoBanners;
